/**
 * Phase 19 / Task 19.2 (DEC-325) — Operations dashboard driver performance table.
 *
 * Columns: loads, BBL, hours, GPS verified %. The spec's further columns
 * (dispatcher completions, sync nudges, miles, on-time %) come from services
 * other sessions are building (DEC-325 D-5). They plug into `extraColumns`, the
 * slot below, so adding one does not touch this table.
 */

import * as React from 'react';
import type { DriverPerformanceRow } from './use-operations-dashboard';
import { formatBbl, formatHours, formatPct } from './dashboard-format';
import { formatNumber } from '@lodex-pro/shared/number-format';

/** One plug-in column: a header and a cell renderer keyed by driver. */
export interface DriverExtraColumn {
  readonly id: string;
  readonly header: string;
  readonly render: (row: DriverPerformanceRow) => React.ReactNode;
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: '11px',
  fontWeight: 700,
  color: 'var(--color-text-tertiary)',
  borderBottom: '1px solid var(--color-border-secondary)',
  whiteSpace: 'nowrap',
};

const numTh: React.CSSProperties = { ...thStyle, textAlign: 'right' };

const tdStyle: React.CSSProperties = {
  padding: '8px 10px',
  fontSize: '13px',
  color: 'var(--color-text-primary)',
  borderBottom: '1px solid var(--color-border-secondary)',
};

const numTd: React.CSSProperties = {
  ...tdStyle,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};

const mutedStyle: React.CSSProperties = { fontSize: '12px', color: 'var(--color-text-tertiary)' };

export interface DriverPerformanceTableProps {
  readonly rows: readonly DriverPerformanceRow[];
  readonly extraColumns?: readonly DriverExtraColumn[];
}

export function DriverPerformanceTable({
  rows,
  extraColumns = [],
}: DriverPerformanceTableProps): React.JSX.Element {
  if (rows.length === 0) {
    return (
      <p style={{ ...mutedStyle, padding: '12px 0' }} data-testid="driver-table-empty">
        No loads were delivered in this date range.
      </p>
    );
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="driver-table">
        <thead>
          <tr>
            <th style={thStyle}>Driver</th>
            <th style={numTh}>Loads</th>
            <th style={numTh}>BBL</th>
            <th style={numTh}>Hours</th>
            <th style={numTh}>GPS verified</th>
            {extraColumns.map((c) => (
              <th key={c.id} style={numTh}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.driverId ?? 'unassigned'}>
              <td style={tdStyle}>
                {r.driverId === null ? (
                  <span style={mutedStyle}>No driver assigned</span>
                ) : (
                  (r.driverName ?? '—')
                )}
              </td>
              <td style={numTd}>{r.loads}</td>
              <td style={numTd}>{formatBbl(r.bbl)}</td>
              <td
                style={numTd}
                title={`${String(r.loadsWithClock)} of ${String(r.loads)} loads timed`}
              >
                {r.loadsWithClock === 0 ? '—' : formatHours(r.activeSecs)}
              </td>
              <td
                style={numTd}
                title={`${String(r.arrivalsVerified)} of ${formatNumber(r.arrivalsTotal)} arrivals verified`}
              >
                {formatPct(r.gpsVerifiedPct)}
              </td>
              {extraColumns.map((c) => (
                <td key={c.id} style={numTd}>
                  {c.render(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
